/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { saveAs } from 'file-saver'
import {
  getCarteiraTenant,
  getCarteiraTenantExport,
  getCarteiraVendedores,
  postReabrirCompetencia,
} from '../../../services/CentralResultados/getCentralResultados'

export const useCentralResultadosDetalhe = () => {
  const { tenantId } = useParams()
  const [detalhe, setDetalhe] = useState<CarteiraTenantType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')

  // Drill por vendedor: qual competencia esta aberta, as linhas e o proprio erro.
  const [drillCompetencia, setDrillCompetencia] = useState<string | null>(null)
  const [vendedores, setVendedores] = useState<CarteiraVendedorType[]>([])
  const [drillLoading, setDrillLoading] = useState(false)
  const [drillErro, setDrillErro] = useState<string | null>(null)
  const [isExportando, setIsExportando] = useState(false)
  // Reentrancia em ref: a exportacao e auditada no servidor, e duplo-clique viraria linha duplicada
  // na trilha.
  const exportandoRef = useRef(false)
  // Mesma protecao para a reabertura, e por motivo mais forte: ela INCREMENTA um contador que a
  // tela exibe para sempre. Duplo-clique marcaria a competencia como reaberta duas vezes.
  const [competenciaReabrindo, setCompetenciaReabrindo] = useState<
    string | null
  >(null)
  const reabrindoRef = useRef(false)

  const montadoRef = useRef(true)
  const drillSourceRef = useRef<ReturnType<
    typeof axios.CancelToken.source
  > | null>(null)
  /** Competencia do drill em voo: barra a resposta atrasada de uma abertura anterior. */
  const drillPedidoRef = useRef<string | null>(null)
  useEffect(() => {
    // ⚠️ Reativar na ENTRADA e obrigatorio, nao simetria decorativa: em StrictMode (React 18, dev)
    // o efeito roda monta -> limpa -> monta, e sem esta linha o cleanup deixaria `montadoRef` em
    // false para sempre. Todo `if (!montadoRef.current) return` passaria a abortar, e a gaveta do
    // drill ficava presa em "carregando" - visto em runtime em 2026-09-02.
    montadoRef.current = true
    return () => {
      montadoRef.current = false
    }
  }, [])

  const fetchDetalhe = async (source: any) => {
    if (!tenantId) return
    setIsLoading(true)
    const result = await getCarteiraTenant(source, tenantId, de, ate)
    if (!montadoRef.current) return
    if ('message' in result) {
      if (result.message !== 'cancelToken') {
        setErro(result.message)
        setDetalhe(null)
        setIsLoading(false)
      }
      return
    }
    setErro(null)
    setDetalhe(result)
    setIsLoading(false)
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchDetalhe(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ tenantId, de, ate })])

  const abrirDrill = async (competencia: string) => {
    if (!tenantId) return
    // Cancela o drill anterior e marca qual competencia e a vigente. Sem isto, abrir A e depois B
    // deixa a resposta de A chegar por ultimo e sobrescrever as linhas de B -- com o titulo e o
    // rodape de conferencia ainda apontando para B. O backend expoe `competencia` em cada linha
    // exatamente para permitir esta checagem; usa-la e a diferenca entre conferir e confiar.
    if (drillSourceRef.current) {
      drillSourceRef.current.cancel('cancelToken')
    }
    const source = axios.CancelToken.source()
    drillSourceRef.current = source
    drillPedidoRef.current = competencia

    setDrillCompetencia(competencia)
    setDrillLoading(true)
    setDrillErro(null)
    setVendedores([])

    const result = await getCarteiraVendedores(source, tenantId, competencia)
    // Descarta resposta atrasada: so aplica se ainda for a competencia pedida.
    if (!montadoRef.current || drillPedidoRef.current !== competencia) return
    if ('message' in result) {
      if (result.message !== 'cancelToken') {
        // Erro do drill fica DENTRO do painel: um toast que some deixaria a gaveta vazia sem
        // explicacao, e "vazio" aqui significaria "este mes nao teve vendedor nenhum".
        setDrillErro(result.message)
        toast.error(result.message)
      }
      setDrillLoading(false)
      return
    }
    // Segunda barreira, agora pelo dado e nao pelo estado local: se a API devolver linhas de outra
    // competencia, nao ha cruzamento silencioso.
    const foraDeOrdem = result.find(v => v.competencia !== competencia)
    if (foraDeOrdem) {
      setDrillErro(
        'A decomposição recebida não corresponde à competência pedida. Reabra o painel.'
      )
      setDrillLoading(false)
      return
    }
    setVendedores(result)
    setDrillLoading(false)
  }

  /**
   * Exporta a série e a decomposição por vendedor no recorte de competências que está na tela.
   *
   * <p>O recorte entra no NOME do arquivo quando existe: exportar jan–jun e depois jul–dez da mesma
   * conta produziria dois arquivos homônimos, e o que circula por e-mail é identificado pelo nome. A
   * aba de metadados guarda a verdade, mas ninguém abre o arquivo para saber qual é qual.
   */
  const exportar = async () => {
    if (!tenantId || exportandoRef.current) return
    exportandoRef.current = true
    setIsExportando(true)
    try {
      const result = await getCarteiraTenantExport(tenantId, de, ate)
      if (result instanceof Blob) {
        const recorte =
          de || ate ? `_${de || 'inicio'}-a-${ate || 'atual'}` : ''
        saveAs(result, `CentralResultados_${tenantId}${recorte}.xlsx`)
      } else {
        toast.error(result.message)
      }
    } finally {
      // Sem o finally, uma falha do `saveAs` deixaria o botão preso em "Gerando…" para sempre.
      exportandoRef.current = false
      setIsExportando(false)
    }
  }

  /**
   * Reabre uma competência fechada, recalcula e fecha de novo.
   *
   * ⚠️ **Recarrega a série INTEIRA no sucesso**, e não só a linha devolvida pela API. Reabrir a
   * competência M altera `clientesPerdidos` de todas as competências ANTERIORES a M (o predicado de
   * "perdido" exige uma competência posterior já fechada). Aplicar só a linha de M deixaria o resto
   * da tabela exibindo números que o servidor acabou de mudar — e ninguém teria motivo para
   * desconfiar deles.
   *
   * ⚠️ A recusa por regra de negócio chega como mensagem e é o VALOR da resposta: ela diz que a
   * janela do ERP já não cobre o mês, ou que a competência não está fechada. Um toast genérico
   * transformaria a proteção mais importante do módulo em "algo deu errado".
   */
  const reabrir = async (competencia: string) => {
    if (!tenantId || reabrindoRef.current) return
    reabrindoRef.current = true
    setCompetenciaReabrindo(competencia)
    try {
      const result = await postReabrirCompetencia(tenantId, competencia)
      if (!montadoRef.current) return
      if ('message' in result) {
        toast.error(result.message)
        return
      }
      toast.success(
        `Competência ${competencia} reaberta, recalculada e fechada de novo.`
      )
      const source = axios.CancelToken.source()
      await fetchDetalhe(source)
    } finally {
      reabrindoRef.current = false
      if (montadoRef.current) setCompetenciaReabrindo(null)
    }
  }

  const fecharDrill = () => {
    // Cancelar e zerar o loading: fechar durante o carregamento deixava a gaveta presa em
    // "carregando" na proxima abertura e a requisicao em voo ainda podia escrever as linhas.
    if (drillSourceRef.current) {
      drillSourceRef.current.cancel('cancelToken')
      drillSourceRef.current = null
    }
    drillPedidoRef.current = null
    setDrillCompetencia(null)
    setVendedores([])
    setDrillErro(null)
    setDrillLoading(false)
  }

  return {
    tenantId,
    detalhe,
    isLoading,
    erro,
    de,
    ate,
    onChangeDe: setDe,
    onChangeAte: setAte,
    drillCompetencia,
    vendedores,
    drillLoading,
    drillErro,
    abrirDrill,
    fecharDrill,
    isExportando,
    exportar,
    competenciaReabrindo,
    reabrir,
  }
}
